import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendEntrepriseContact } from '../services/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import '../styles/ContactEntreprise.css';

const ContactEntreprise = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    entreprise: '',
    nomRecruteur: '',
    email: '',
    telephone: '',
    poste: '',
    typeContrat: '',
    localisation: '',
    remote: '',
    salaire: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const typesContrat = [
    { value: 'cdi', label: t('entreprise.contractType.cdi') },
    { value: 'cdd', label: t('entreprise.contractType.cdd') },
    { value: 'freelance', label: t('entreprise.contractType.freelance') },
  ];

  const remoteOptions = [
    { value: 'full', label: t('entreprise.remote.full') },
    { value: 'hybride', label: t('entreprise.remote.hybrid') },
    { value: 'presentiel', label: t('entreprise.remote.onsite') },
    { value: 'flexible', label: t('entreprise.remote.flexible') }
  ];

  const salaireRanges = [
    { value: '30-40k', label: t('entreprise.salary.30-40k') },
    { value: '40-50k', label: t('entreprise.salary.40-50k') },
    { value: '50-60k', label: t('entreprise.salary.50-60k') },
    { value: '60k+', label: t('entreprise.salary.60k+') },
    { value: 'tjm', label: t('entreprise.salary.tjm') },
    { value: 'negocier', label: t('entreprise.salary.negotiate') }
  ];

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^[0-9+\s()-]{10,}$/;
    return re.test(phone);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.entreprise.trim()) {
      newErrors.entreprise = t('entreprise.validation.companyRequired');
    }

    if (!formData.nomRecruteur.trim()) {
      newErrors.nomRecruteur = t('entreprise.validation.nameRequired');
    }

    if (!formData.email.trim()) {
      newErrors.email = t('entreprise.validation.emailRequired');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('entreprise.validation.emailInvalid');
    }

    if (formData.telephone && !validatePhone(formData.telephone)) {
      newErrors.telephone = t('entreprise.validation.phoneInvalid');
    }

    if (!formData.poste.trim()) {
      newErrors.poste = t('entreprise.validation.positionRequired');
    }

    if (!formData.typeContrat) {
      newErrors.typeContrat = t('entreprise.validation.contractTypeRequired');
    }

    if (!formData.remote) {
      newErrors.remote = t('entreprise.validation.remoteRequired');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('entreprise.validation.descriptionRequired');
    } else if (formData.description.trim().length < 50) {
      newErrors.description = t('entreprise.validation.descriptionTooShort');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await sendEntrepriseContact(formData);

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          entreprise: '',
          nomRecruteur: '',
          email: '',
          telephone: '',
          poste: '',
          typeContrat: '',
          localisation: '',
          remote: '',
          salaire: '',
          description: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-entreprise-container">
      {/* <LanguageSwitcher /> */}
      
      <div className="contact-entreprise-header">
        <h1 className="contact-entreprise-title">{t('entreprise.title')}</h1>
        <p className="contact-entreprise-subtitle">
          {t('entreprise.subtitle')}
        </p>
      </div>

      <form className="contact-form-entreprise" onSubmit={handleSubmit}>
        {/* Informations entreprise */}
        <div className="form-section">
          <h2 className="form-section-title">{t('entreprise.section.company')}</h2>
          
          <div className="form-group">
            <label htmlFor="entreprise" className="form-label">
              {t('entreprise.label.companyName')} <span className="required">{t('common.required')}</span>
            </label>
            <input
              type="text"
              id="entreprise"
              name="entreprise"
              value={formData.entreprise}
              onChange={handleChange}
              className={`form-input ${errors.entreprise ? 'error' : ''}`}
              placeholder={t('entreprise.placeholder.companyName')}
            />
            {errors.entreprise && <span className="error-message">{errors.entreprise}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="nomRecruteur" className="form-label">
                {t('entreprise.label.recruiterName')} <span className="required">{t('common.required')}</span>
              </label>
              <input
                type="text"
                id="nomRecruteur"
                name="nomRecruteur"
                value={formData.nomRecruteur}
                onChange={handleChange}
                className={`form-input ${errors.nomRecruteur ? 'error' : ''}`}
                placeholder={t('entreprise.placeholder.recruiterName')}
              />
              {errors.nomRecruteur && <span className="error-message">{errors.nomRecruteur}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                {t('entreprise.label.email')} <span className="required">{t('common.required')}</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-input ${errors.email ? 'error' : ''}`}
                placeholder={t('entreprise.placeholder.email')}
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="telephone" className="form-label">
              {t('entreprise.label.phone')}
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              className={`form-input ${errors.telephone ? 'error' : ''}`}
              placeholder={t('entreprise.placeholder.phone')}
            />
            {errors.telephone && <span className="error-message">{errors.telephone}</span>}
          </div>
        </div>

        {/* Détails du poste */}
        <div className="form-section">
          <h2 className="form-section-title">{t('entreprise.section.position')}</h2>
          
          <div className="form-group">
            <label htmlFor="poste" className="form-label">
              {t('entreprise.label.position')} <span className="required">{t('common.required')}</span>
            </label>
            <input
              type="text"
              id="poste"
              name="poste"
              value={formData.poste}
              onChange={handleChange}
              className={`form-input ${errors.poste ? 'error' : ''}`}
              placeholder={t('entreprise.placeholder.position')}
            />
            {errors.poste && <span className="error-message">{errors.poste}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('entreprise.label.contractType')} <span className="required">{t('common.required')}</span>
            </label>
            <div className="radio-group">
              {typesContrat.map(type => (
                <label key={type.value} className="radio-label">
                  <input
                    type="radio"
                    name="typeContrat"
                    value={type.value}
                    checked={formData.typeContrat === type.value}
                    onChange={handleChange}
                    className="radio-input"
                  />
                  <span className="radio-custom">{type.label}</span>
                </label>
              ))}
            </div>
            {errors.typeContrat && <span className="error-message">{errors.typeContrat}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="localisation" className="form-label">
                {t('entreprise.label.location')}
              </label>
              <input
                type="text"
                id="localisation"
                name="localisation"
                value={formData.localisation}
                onChange={handleChange}
                className="form-input"
                placeholder={t('entreprise.placeholder.location')}
              />
            </div>

            <div className="form-group">
              <label htmlFor="salaire" className="form-label">
                {t('entreprise.label.salary')}
              </label>
              <select
                id="salaire"
                name="salaire"
                value={formData.salaire}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">{t('entreprise.placeholder.salary')}</option>
                {salaireRanges.map(range => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              {t('entreprise.label.remote')} <span className="required">{t('common.required')}</span>
            </label>
            <div className="radio-group">
              {remoteOptions.map(option => (
                <label key={option.value} className="radio-label">
                  <input
                    type="radio"
                    name="remote"
                    value={option.value}
                    checked={formData.remote === option.value}
                    onChange={handleChange}
                    className="radio-input"
                  />
                  <span className="radio-custom">{option.label}</span>
                </label>
              ))}
            </div>
            {errors.remote && <span className="error-message">{errors.remote}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description" className="form-label">
              {t('entreprise.label.description')} <span className="required">{t('common.required')}</span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`form-textarea ${errors.description ? 'error' : ''}`}
              placeholder={t('entreprise.placeholder.description')}
              rows="8"
            />
            <div className="textarea-footer">
              <span className="char-count">
                {formData.description.length} / 2000 {t('entreprise.charCount')}
              </span>
              {errors.description && <span className="error-message">{errors.description}</span>}
            </div>
          </div>
        </div>

        {/* Submit status messages */}
        {submitStatus === 'success' && (
          <div className="success-message">
            {t('entreprise.success')}
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="error-message-box">
            {t('entreprise.error')}
          </div>
        )}

        {/* Submit button */}
        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              {t('entreprise.button.submitting')}
            </>
          ) : (
            <>
              {t('entreprise.button.submit')}
              <span className="button-arrow">→</span>
            </>
          )}
        </button>

        <p className="form-footer-text">
          {t('entreprise.footer')}
        </p>
      </form>
    </div>
  );
};

export default ContactEntreprise;